import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import { Box, Button, Grid2 as Grid, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import creditCardType from 'credit-card-type';
import { map } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import Mastercard from '../../../assets/icons/mastercard.svg';
import Visa from '../../../assets/icons/visa.svg';
import IsMountedWrapper from '../../../utils/functions/isMountedWrapper';
import { BookingStore } from '../../../utils/stores/BookingStore';
import StripeFallbackForm from './StripeFallbackForm';
import { commonStyles } from './commonStyles';

interface ErrorObject {
    readonly error?: boolean;
    readonly message?: string;
}

interface Errors {
    readonly name?: ErrorObject;
    readonly number?: ErrorObject;
    readonly expiry?: ErrorObject;
    readonly cvn?: ErrorObject;
}

const formatCardNumber = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
};

// A single MM / YY field, matching how Stripe renders expiry on the fallback form.
// The separator is only added once a third digit arrives: inserting it on the second
// keystroke would make it impossible to backspace past, since reformatting would
// immediately put it back.
const formatExpiry = (raw: string) => {
    let digits = raw.replace(/\D/g, '');
    if (digits.length === 0) {
        return '';
    }
    // A leading digit above 1 can only be a single-digit month, so pad it: 5 -> 05.
    // This also rescues browser autofill, which supplies values like "5/2030".
    if (/[2-9]/.test(digits[0])) {
        digits = `0${digits}`;
    }
    // Autofill commonly supplies a four digit year; keep the last two.
    if (digits.length > 4) {
        digits = `${digits.slice(0, 2)}${digits.slice(-2)}`;
    }
    // Drop an impossible month rather than letting it through to eWAY as V6034.
    const month = Number(digits.slice(0, 2));
    if (digits.length >= 2 && (month < 1 || month > 12)) {
        digits = digits.slice(0, 1);
    }
    return digits.length > 2 ? `${digits.slice(0, 2)} / ${digits.slice(2)}` : digits;
};

const expiryParts = (formatted: string) => {
    const digits = formatted.replace(/\D/g, '');
    return { month: digits.slice(0, 2), year: digits.slice(2, 4) };
};

const formatCvn = (raw: string) => raw.replace(/\D/g, '').slice(0, 3);

const CreditCardDetails = () => {
    const bookingController = useContext(BookingStore);
    const isMounted = IsMountedWrapper();
    const theme = useTheme();
    const xs = useMediaQuery(theme.breakpoints.only('xs'));
    const [localCard, setLocalCard] = useState('');
    const [localCVN, setLocalCVN] = useState('');
    const [localExpiry, setLocalExpiry] = useState('');
    const [errors, setErrors] = useState<Errors>({});
    const [cardType, setCardType] = useState('');
    const fallbackActive = bookingController?.payment?.data?.fallbackActive;

    // The store drops the card values it holds when eWay declines; clear the copies kept
    // here for display too, so no card data survives into the second attempt.
    useEffect(() => {
        if (fallbackActive) {
            setLocalCard('');
            setLocalCVN('');
            setLocalExpiry('');
            setCardType('');
            setErrors({});
        }
    }, [fallbackActive]);

    const emitCardChange = (name: string, value: string, isEncrypted: boolean) => {
        bookingController.updateCardDetails({ target: { name, value } }, isEncrypted);
    };

    const checkErrors = () => {
        const errorObject: Errors = {
            name: !bookingController?.payment?.data?.cardDetails?.Name
                ? {
                      error: true,
                      message: 'Card Name is required',
                  }
                : { error: false },
            number:
                localCard.replace(/\s/g, '').length !== 16
                    ? {
                          error: true,
                          message: 'Check you have entered all digits',
                      }
                    : cardType === 'american-express' || ['visa', 'mastercard'].indexOf(cardType) === -1
                      ? {
                            error: true,
                            message: 'Please use Mastercard or Visa',
                        }
                      : // Encryption returns null when the eWAY CSE key is missing; the digits
                        // look valid but nothing usable was sent. Fail loudly rather than
                        // submitting an empty card number (eWAY V6022).
                        !bookingController?.payment?.data?.cardDetails?.Number?.startsWith('eCrypted:')
                        ? {
                              error: true,
                              message: 'Secure payment is temporarily unavailable. Please refresh and try again.',
                          }
                        : { error: false },
            expiry:
                expiryParts(localExpiry).month.length !== 2 || expiryParts(localExpiry).year.length !== 2
                    ? {
                          error: true,
                          message: 'Check expiry date',
                      }
                    : { error: false },
            cvn:
                localCVN.replace(/\s/g, '').length !== 3
                    ? {
                          error: true,
                          message: 'Check CVC',
                      }
                    : !bookingController?.payment?.data?.cardDetails?.CVN?.startsWith('eCrypted:')
                      ? {
                            error: true,
                            message: 'Secure payment is temporarily unavailable. Please refresh and try again.',
                        }
                      : { error: false },
        };
        setErrors(errorObject);
        return errorObject;
    };

    const checkCardType = (cardNum: string) => {
        const result = creditCardType(cardNum)[0];
        const type = result?.type;
        cardNum.replace(/\s/g, '').length > 0 ? setCardType(type) : setCardType('');
    };

    const SubmitButton = () => {
        return (
            <Grid container size={{ xs: 12 }} alignItems="center" style={{ padding: xs ? '10px 5px' : 10 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Button
                        sx={commonStyles.bookingButton}
                        variant="contained"
                        disabled={bookingController.isLoading}
                        onClick={() => {
                            !map(checkErrors(), (err) => err.error).includes(true) &&
                                bookingController.processBooking();
                        }}
                    >
                        {bookingController.isLoading ? (
                            <CircularProgress variant="indeterminate" disableShrink size={24} thickness={4} />
                        ) : (
                            'Submit Booking'
                        )}
                    </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }} sx={commonStyles.encryptSect}>
                    <LockIcon style={{ color: 'rgb(0 168 0)', margin: '0 10px 0 15px' }} />
                    <Typography style={{ color: 'rgb(0 168 0)', padding: '14px 0', marginRight: 15 }}>
                        256 Bit Encryption
                    </Typography>
                </Grid>
            </Grid>
        );
    };

    return (
        <>
            {bookingController?.payment?.data?.paymentError ? (
                <Grid container>
                    <Typography variant="h6" style={{ color: 'red', paddingBottom: 10, width: '100%' }}>
                        Payment Error
                    </Typography>
                    <Typography style={{ fontSize: 16, color: theme.palette.text.soft, marginBottom: 20 }}>
                        Uh oh! We had a problem authorising your payment. No charge has been made. Please check that you
                        have entered your card details correctly and try again. If you&apos;re unable to complete your
                        payment online, please contact us and we will happily book your tour over the phone. Click below
                        to clear the error.
                    </Typography>
                    <Button
                        sx={commonStyles.errorButton}
                        variant="outlined"
                        onClick={() => {
                            bookingController.payment.setData(
                                (prev) => ({
                                    ...prev,
                                    paymentError: false,
                                }),
                                isMounted
                            );
                        }}
                    >
                        OK, Got It
                    </Button>
                </Grid>
            ) : fallbackActive ? (
                // eWay declined with a code a different acquirer may approve, so the card
                // fields are swapped for Stripe's and the customer simply tries again.
                <StripeFallbackForm />
            ) : bookingController.booking?.bookingResponse?.error ? (
                <Grid container style={{ color: theme.palette.text.soft }}>
                    <Typography variant="h6" style={{ color: 'red', paddingBottom: 10, width: '100%' }}>
                        Booking Error
                    </Typography>
                    <Typography>
                        We were able to authorise your card, but something went wrong while finalising your booking with
                        our system.
                    </Typography>
                    <Typography component="div" style={{ margin: '10px 0' }}>
                        <Box display="inline" style={{ color: 'rgb(0 168 0)' }}>
                            Good news: you have not been charged.
                        </Box>{' '}
                        The temporary authorisation hold on your card has been released automatically.
                    </Typography>
                    <Typography>
                        Our reservations team has been notified with your details and will be in touch shortly to
                        complete your booking. There&apos;s nothing further you need to do, but feel free to contact us
                        if you&apos;d like to confirm right away.
                    </Typography>
                    <Typography style={{ margin: '10px 0' }}>We sincerely apologise for the inconvenience.</Typography>
                </Grid>
            ) : (
                <Grid container sx={commonStyles.cardContainer}>
                    <Grid
                        container
                        alignItems="center"
                        size={{ xs: 12 }}
                        style={{
                            padding: xs ? '10px 5px' : '0 10px 10px',
                            marginBottom: 5,
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 10,
                            width: '100%',
                        }}
                    >
                        <img
                            src={Visa}
                            alt="visa_payment"
                            style={{ height: xs ? 35 : 40, opacity: cardType === 'mastercard' ? 0.4 : 1 }}
                        />
                        <img
                            src={Mastercard}
                            alt="mastercard_payment"
                            style={{ height: xs ? 35 : 40, opacity: cardType === 'visa' ? 0.4 : 1 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }} style={{ padding: xs ? '10px 5px' : 10 }}>
                        <TextField
                            style={{ width: '100%' }}
                            label="Name on Card"
                            variant="outlined"
                            color="primary"
                            type="text"
                            name="Name"
                            value={bookingController?.payment?.data?.cardDetails?.Name || ''}
                            error={errors?.name?.error}
                            helperText={errors?.name?.message}
                            onChange={(event) => bookingController.updateCardDetails(event, false)}
                            required={true}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <PersonIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }} style={{ padding: xs ? '10px 5px' : 10 }}>
                        <TextField
                            style={{ flex: 'auto', width: '100%' }}
                            label="Card Number"
                            variant="outlined"
                            color="primary"
                            type="text"
                            name="Number"
                            placeholder="1234 1234 1234 1234"
                            inputMode="numeric"
                            autoComplete="cc-number"
                            value={localCard}
                            error={errors?.number?.error}
                            helperText={errors?.number?.message}
                            required={true}
                            // MUI hides the placeholder until the label shrinks away. The
                            // Stripe fields pin their labels out of necessity, so pin these
                            // too or the two forms show different empty states.
                            InputLabelProps={{ shrink: true }}
                            onChange={(event) => {
                                const formatted = formatCardNumber(event.target.value);
                                // eWAY must receive the raw digits (no spaces) to encrypt,
                                // otherwise it can't read the card number (V6022).
                                emitCardChange('Number', formatted.replace(/\s/g, ''), true);
                                setLocalCard(formatted);
                                checkCardType(formatted);
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {cardType === 'visa' ? (
                                            <img src={Visa} alt="visa icon" style={{ height: 35 }} />
                                        ) : cardType === 'mastercard' ? (
                                            <img src={Mastercard} alt="mastercard icon" style={{ height: 35 }} />
                                        ) : (
                                            <CreditCardIcon color="action" />
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid container size={{ xs: 12 }}>
                        <Grid size={{ xs: 12, sm: 6 }} style={{ padding: xs ? '10px 5px' : 10 }}>
                            <TextField
                                label="Expiry"
                                style={{ width: '100%' }}
                                variant="outlined"
                                color="primary"
                                type="text"
                                name="Expiry"
                                placeholder="MM / YY"
                                inputMode="numeric"
                                autoComplete="cc-exp"
                                value={localExpiry}
                                error={errors?.expiry?.error}
                                helperText={errors?.expiry?.message}
                                required={true}
                                InputLabelProps={{ shrink: true }}
                                onChange={(event) => {
                                    const formatted = formatExpiry(event.target.value);
                                    setLocalExpiry(formatted);
                                    // eWAY still wants the two parts separately.
                                    const { month, year } = expiryParts(formatted);
                                    emitCardChange('ExpiryMonth', month, false);
                                    emitCardChange('ExpiryYear', year, false);
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <CalendarTodayIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }} style={{ padding: xs ? '10px 5px' : 10 }}>
                            <TextField
                                label="CVC"
                                style={{ width: '100%' }}
                                variant="outlined"
                                color="primary"
                                type="text"
                                name="CVN"
                                placeholder="CVC"
                                inputMode="numeric"
                                autoComplete="cc-csc"
                                value={localCVN}
                                error={errors?.cvn?.error}
                                helperText={errors?.cvn?.message}
                                required={true}
                                InputLabelProps={{ shrink: true }}
                                onChange={(event) => {
                                    const formatted = formatCvn(event.target.value);
                                    emitCardChange('CVN', formatted, true);
                                    setLocalCVN(formatted);
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <LockIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                    <SubmitButton />
                </Grid>
            )}
        </>
    );
};

export default CreditCardDetails;
